import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { STORAGE_KEYS } from '../constants';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeOption {
  value: ThemePreference;
  labelKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly destroyRef = inject(DestroyRef);

  readonly themeOptions: ThemeOption[] = [
    { value: 'light', labelKey: 'settings.themeLight' },
    { value: 'dark', labelKey: 'settings.themeDark' },
    { value: 'system', labelKey: 'settings.themeSystem' },
  ];

  private preferenceSignal = signal<ThemePreference>('system');
  private systemDarkSignal = signal(this.getSystemPrefersDark());

  readonly preference = this.preferenceSignal.asReadonly();
  readonly resolvedTheme = computed<ResolvedTheme>(() => {
    const pref = this.preferenceSignal();
    if (pref === 'system') {
      return this.systemDarkSignal() ? 'dark' : 'light';
    }
    return pref;
  });

  constructor() {
    this.initialize();
    this.setupSystemListener();
  }

  setTheme(pref: ThemePreference): void {
    if (!this.isValidPreference(pref)) {
      return;
    }
    this.preferenceSignal.set(pref);
    localStorage.setItem(STORAGE_KEYS.THEME, pref);
    this.applyTheme();
  }

  private initialize(): void {
    const saved = localStorage.getItem(STORAGE_KEYS.THEME) as ThemePreference | null;
    const pref = saved && this.isValidPreference(saved) ? saved : 'system';
    this.preferenceSignal.set(pref);
    this.applyTheme();
  }

  private applyTheme(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const isDark = this.resolvedTheme() === 'dark';
    const html = document.documentElement;

    html.classList.toggle('dark', isDark);
    html.classList.toggle('ion-palette-dark', isDark);
    html.style.colorScheme = isDark ? 'dark' : 'light';

    const themeColor = document.querySelector('meta[name="theme-color"]');
    themeColor?.setAttribute('content', isDark ? '#111827' : '#059669');

    const colorScheme = document.querySelector('meta[name="color-scheme"]');
    colorScheme?.setAttribute('content', isDark ? 'dark' : 'light');
  }

  private setupSystemListener(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      this.systemDarkSignal.set(mediaQuery.matches);
      if (this.preferenceSignal() === 'system') {
        this.applyTheme();
      }
    };

    mediaQuery.addEventListener('change', handler);
    this.destroyRef.onDestroy(() => mediaQuery.removeEventListener('change', handler));
  }

  private getSystemPrefersDark(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  private isValidPreference(value: string): value is ThemePreference {
    return value === 'light' || value === 'dark' || value === 'system';
  }
}
